// client/src/coms/voip/audioEffects.js
const DEFAULT_PRESET = 'dry';

function clamp(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

function safeCtx() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    return new Ctx();
  } catch {
    return null;
  }
}

export function ensureAudioEffectsContext(existingCtx = null) {
  if (existingCtx) return existingCtx;
  return safeCtx();
}

export async function resumeAudioEffectsContext(ctx) {
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
  } catch {}
}

function setParamValue(param, value, now, smoothing = 0.02) {
  if (!param) return;
  const v = Number(value);
  if (!Number.isFinite(v)) return;
  try {
    param.cancelScheduledValues(now);
    param.setTargetAtTime(v, now, smoothing);
  } catch {
    try {
      param.value = v;
    } catch {}
  }
}

function setWetDry(rack, wet, now, smoothing = 0.02) {
  const w = clamp(wet, 0, 1, 0);
  const d = 1 - w;
  setParamValue(rack.dryGain.gain, d, now, smoothing);
  setParamValue(rack.wetGain.gain, w, now, smoothing);
}

function splitReverbAmount(amount) {
  const a = clamp(amount, 0, 10, 0);
  if (a <= 2) {
    return {
      base: a / 2,
      boost: 0,
    };
  }
  return {
    base: 1,
    boost: (a - 2) / 8,
  };
}

function mapReverbAmountToWetMix(amount) {
  const { base, boost } = splitReverbAmount(amount);
  const baseWet = 0.01 + (base * 0.17);
  const extraWet = boost * 0.20;
  return Math.min(0.38, baseWet + extraWet);
}

function mapReverbAmountToEarlyGain(amount) {
  const { base, boost } = splitReverbAmount(amount);
  const baseGain = 0.035 + (base * 0.115);
  const extraGain = boost * 0.05;
  return Math.min(0.22, baseGain + extraGain);
}

function mapReverbAmountToLateSend(amount) {
  const { base, boost } = splitReverbAmount(amount);
  const baseSend = 0.18 + (base * 0.34);
  const extraSend = boost * 0.34;
  return Math.min(0.86, baseSend + extraSend);
}

function mapReverbAmountToDecayScale(amount) {
  const { base, boost } = splitReverbAmount(amount);
  const baseScale = 0.92 + (base * 0.38);
  const extraScale = boost * 0.85;
  return Math.min(2.15, baseScale + extraScale);
}

function mapReverbAmountToWetLowpass(amount, baseHz) {
  const { boost } = splitReverbAmount(amount);
  return Math.max(3200, baseHz - (boost * 900));
}

function mapReverbAmountToOutputTrim(amount, baseTrim, compressionAmount = 0) {
  const { boost } = splitReverbAmount(amount);
  const compressionTrim = clamp(compressionAmount, 0, 10, 0) * 0.008;
  return Math.max(0.72, baseTrim - (boost * 0.06) - compressionTrim);
}

function getCompressionSettings(amount) {
  const a = clamp(amount, 0, 10, 0);
  const t = a / 10;
  return {
    threshold: -24 - (t * 18),
    knee: 24 - (t * 10),
    ratio: 1 + (t * 7),
    attack: 0.012 - (t * 0.008),
    release: 0.26 - (t * 0.1),
    makeup: 1 + (t * 1.1),
  };
}

function getBrightnessSettings(amount) {
  const a = clamp(amount, 0, 10, 5);
  const t = a / 10;
  const centered = (a - 5) / 5;

  return {
    inputLowpassHz: 2500 + (t * 9500),
    dryPresenceFreqHz: 1600 + (t * 2600),
    dryPresenceGain: -10 + (t * 20),
    wetPresenceFreqHz: 1800 + (t * 2400),
    wetPresenceGain: -8 + (t * 16),
    airFreqHz: 3200 + (t * 4200),
    airGain: -10 + (t * 20),
    wetLowpassOffsetHz: -2200 + (t * 4400),
    wetMixMul: 0.82 + (t * 0.36),
    convolverToneMul: 0.82 + (t * 0.36),
    earlyBusMul: 0.9 + (t * 0.22),
    outputTrimOffset: -(centered * 0.035),
  };
}

function getCharacterSettings(character) {
  const c = String(character || 'medium').trim().toLowerCase();

  if (c === 'small') {
    return {
      key: 'small',
      preDelay: 0.012,
      inputHighpass: 150,
      inputLowpass: 8600,
      wetHighpass: 170,
      wetLowpass: 7100,
      outputTrim: 0.93,
      wetTrim: 0.95,
      baseDecay: 1.15,
      density: 0.8,
      earlyTimes: [0.008, 0.014, 0.021, 0.031],
      earlyPans: [-0.45, 0.3, -0.2, 0.42],
      earlyDecayCurve: 0.86,
      stereoWidth: 0.6,
      tailTone: 0.78,
      lateBloom: 0.16,
    };
  }

  if (c === 'large') {
    return {
      key: 'large',
      preDelay: 0.026,
      inputHighpass: 130,
      inputLowpass: 7000,
      wetHighpass: 135,
      wetLowpass: 5600,
      outputTrim: 0.87,
      wetTrim: 1.02,
      baseDecay: 2.05,
      density: 1.15,
      earlyTimes: [0.012, 0.021, 0.034, 0.051],
      earlyPans: [-0.52, 0.4, -0.28, 0.55],
      earlyDecayCurve: 0.91,
      stereoWidth: 0.9,
      tailTone: 0.68,
      lateBloom: 0.25,
    };
  }

  return {
    key: 'medium',
    preDelay: 0.018,
    inputHighpass: 140,
    inputLowpass: 7800,
    wetHighpass: 150,
    wetLowpass: 6300,
    outputTrim: 0.9,
    wetTrim: 0.98,
    baseDecay: 1.55,
    density: 1,
    earlyTimes: [0.01, 0.017, 0.026, 0.039],
    earlyPans: [-0.48, 0.34, -0.24, 0.47],
    earlyDecayCurve: 0.89,
    stereoWidth: 0.76,
    tailTone: 0.73,
    lateBloom: 0.2,
  };
}

export function getAudioEffectPreset(name) {
  const key = String(name || DEFAULT_PRESET).trim().toLowerCase();

  if (key === 'dry') {
    return {
      name: 'dry',
      reverbAmount: 0,
      reverbCharacter: 'medium',
      compressionAmount: 0,
      brightnessAmount: 5,
    };
  }

  return {
    name: DEFAULT_PRESET,
    reverbAmount: 0,
    reverbCharacter: 'medium',
    compressionAmount: 0,
    brightnessAmount: 5,
  };
}

export function listAudioEffectPresetNames() {
  return [DEFAULT_PRESET];
}

function createPanNode(ctx, panValue = 0) {
  if (ctx && typeof ctx.createStereoPanner === 'function') {
    const panner = ctx.createStereoPanner();
    panner.pan.value = clamp(panValue, -1, 1, 0);
    return {
      input: panner,
      output: panner,
      setPan(value, now, smoothing) {
        setParamValue(panner.pan, clamp(value, -1, 1, 0), now, smoothing);
      },
      disconnect() {
        try {
          panner.disconnect();
        } catch {}
      },
    };
  }

  const input = ctx.createGain();
  const splitter = ctx.createChannelSplitter(2);
  const left = ctx.createGain();
  const right = ctx.createGain();
  const merger = ctx.createChannelMerger(2);

  input.connect(splitter);
  splitter.connect(left, 0);
  splitter.connect(right, 0);
  left.connect(merger, 0, 0);
  right.connect(merger, 0, 1);

  const applyPan = value => {
    const p = clamp(value, -1, 1, 0);
    const leftGain = p <= 0 ? 1 : 1 - p;
    const rightGain = p >= 0 ? 1 : 1 + p;
    left.gain.value = leftGain;
    right.gain.value = rightGain;
  };

  applyPan(panValue);

  return {
    input,
    output: merger,
    setPan(value) {
      applyPan(value);
    },
    disconnect() {
      try {
        input.disconnect();
      } catch {}
      try {
        splitter.disconnect();
      } catch {}
      try {
        left.disconnect();
      } catch {}
      try {
        right.disconnect();
      } catch {}
      try {
        merger.disconnect();
      } catch {}
    },
  };
}

function createEarlyTap(ctx, sourceNode, wetBus, pan = 0) {
  const delay = ctx.createDelay(1.0);
  const gain = ctx.createGain();
  const panNode = createPanNode(ctx, pan);

  gain.gain.value = 0;

  sourceNode.connect(delay);
  delay.connect(gain);
  gain.connect(panNode.input);
  panNode.output.connect(wetBus);

  return { delay, gain, panNode };
}

function createSeededRandom(seed) {
  let s = seed >>> 0;
  return function next() {
    s = (s * 1664525 + 1013904223) >>> 0;
    return (s / 4294967295) * 2 - 1;
  };
}

function buildVocalImpulseResponse(ctx, character, amount) {
  const sampleRate = ctx.sampleRate || 48000;
  const decayScale = mapReverbAmountToDecayScale(amount);
  const decay = character.baseDecay * decayScale;
  const length = Math.max(1, Math.floor(sampleRate * decay));
  const channels = 2;
  const buffer = ctx.createBuffer(channels, length, sampleRate);
  const randL = createSeededRandom(1337 + Math.round(amount * 97));
  const randR = createSeededRandom(7331 + Math.round(amount * 131));

  const width = clamp(character.stereoWidth, 0, 1, 0.75);
  const density = clamp(character.density, 0.4, 1.5, 1);
  const lateBloom = clamp(character.lateBloom, 0, 0.5, 0.2);
  const tailTone = clamp(character.tailTone, 0.35, 1, 0.72);

  for (let ch = 0; ch < channels; ch++) {
    const data = buffer.getChannelData(ch);
    const rand = ch === 0 ? randL : randR;
    let last = 0;

    for (let i = 0; i < length; i++) {
      const t = i / length;
      const envA = Math.pow(1 - t, 1.25);
      const envB = Math.exp(-t * (3.2 / Math.max(0.25, decay)));
      const bloom = 1 + (Math.sin(Math.PI * Math.min(1, t * 2.5)) * lateBloom);
      const hfLoss = 1 - (t * (1 - tailTone));
      const noise = rand();
      const filtered = (last * (0.78 - (t * 0.1))) + (noise * 0.22 * density);
      last = filtered;

      const stereoBias = ch === 0 ? (1 - width * 0.22) : (1 + width * 0.22);
      const shimmer = Math.sin((2 * Math.PI * i / sampleRate) * (420 + (ch * 37))) * 0.0025;
      data[i] = filtered * envA * envB * bloom * hfLoss * stereoBias + shimmer;
    }
  }

  const spikeCount = 18;
  for (let i = 0; i < spikeCount; i++) {
    const tBase = 0.009 + (i * (decay / (spikeCount + 4)));
    const offset = ((i % 2 === 0 ? 1 : -1) * 0.0018 * width);
    const idxL = Math.min(length - 1, Math.max(0, Math.floor((tBase + offset) * sampleRate)));
    const idxR = Math.min(length - 1, Math.max(0, Math.floor((tBase - offset) * sampleRate)));
    const amp = Math.pow(1 - (i / spikeCount), character.earlyDecayCurve) * 0.16;

    buffer.getChannelData(0)[idxL] += amp;
    buffer.getChannelData(1)[idxR] += amp;
  }

  return buffer;
}

function createConvolverStage(ctx, sourceNode, wetBus) {
  const inputGain = ctx.createGain();
  const convolver = ctx.createConvolver();
  const toneHighpass = ctx.createBiquadFilter();
  const toneLowpass = ctx.createBiquadFilter();
  const outputGain = ctx.createGain();

  toneHighpass.type = 'highpass';
  toneHighpass.frequency.value = 150;

  toneLowpass.type = 'lowpass';
  toneLowpass.frequency.value = 6300;

  inputGain.gain.value = 0.4;
  outputGain.gain.value = 1;

  sourceNode.connect(inputGain);
  inputGain.connect(convolver);
  convolver.connect(toneHighpass);
  toneHighpass.connect(toneLowpass);
  toneLowpass.connect(outputGain);
  outputGain.connect(wetBus);

  return {
    inputGain,
    convolver,
    toneHighpass,
    toneLowpass,
    outputGain,
  };
}

export function createAudioEffectsRack(ctx) {
  if (!ctx) return null;

  const input = ctx.createGain();
  const output = ctx.createGain();

  const inputCompressor = ctx.createDynamicsCompressor();
  inputCompressor.threshold.value = 0;
  inputCompressor.knee.value = 24;
  inputCompressor.ratio.value = 1;
  inputCompressor.attack.value = 0.01;
  inputCompressor.release.value = 0.26;

  const compressorMakeupGain = ctx.createGain();
  compressorMakeupGain.gain.value = 1;

  const dryTone = ctx.createBiquadFilter();
  dryTone.type = 'peaking';
  dryTone.frequency.value = 2600;
  dryTone.Q.value = 0.8;
  dryTone.gain.value = 0;

  const dryGain = ctx.createGain();
  dryGain.gain.value = 1;

  const wetGain = ctx.createGain();
  wetGain.gain.value = 0;

  const preDelay = ctx.createDelay(1.0);
  preDelay.delayTime.value = 0.018;

  const inputHighpass = ctx.createBiquadFilter();
  inputHighpass.type = 'highpass';
  inputHighpass.frequency.value = 140;

  const inputLowpass = ctx.createBiquadFilter();
  inputLowpass.type = 'lowpass';
  inputLowpass.frequency.value = 7800;

  const earlyBus = ctx.createGain();
  earlyBus.gain.value = 1;

  const lateInputBus = ctx.createGain();
  lateInputBus.gain.value = 1;

  const wetHighpass = ctx.createBiquadFilter();
  wetHighpass.type = 'highpass';
  wetHighpass.frequency.value = 150;

  const wetLowpass = ctx.createBiquadFilter();
  wetLowpass.type = 'lowpass';
  wetLowpass.frequency.value = 6300;

  const wetPresence = ctx.createBiquadFilter();
  wetPresence.type = 'peaking';
  wetPresence.frequency.value = 2600;
  wetPresence.Q.value = 0.8;
  wetPresence.gain.value = 1.2;

  const wetAir = ctx.createBiquadFilter();
  wetAir.type = 'highshelf';
  wetAir.frequency.value = 5200;
  wetAir.gain.value = 0;

  const wetLimiter = ctx.createDynamicsCompressor();
  wetLimiter.threshold.value = -19;
  wetLimiter.knee.value = 8;
  wetLimiter.ratio.value = 9;
  wetLimiter.attack.value = 0.004;
  wetLimiter.release.value = 0.18;

  const masterLimiter = ctx.createDynamicsCompressor();
  masterLimiter.threshold.value = -10;
  masterLimiter.knee.value = 9;
  masterLimiter.ratio.value = 12;
  masterLimiter.attack.value = 0.004;
  masterLimiter.release.value = 0.16;

  const outputGain = ctx.createGain();
  outputGain.gain.value = 0.9;

  input.connect(inputCompressor);
  inputCompressor.connect(compressorMakeupGain);

  compressorMakeupGain.connect(dryTone);
  dryTone.connect(dryGain);
  dryGain.connect(masterLimiter);

  compressorMakeupGain.connect(preDelay);
  preDelay.connect(inputHighpass);
  inputHighpass.connect(inputLowpass);

  const earlyTaps = [
    createEarlyTap(ctx, inputLowpass, earlyBus, -0.48),
    createEarlyTap(ctx, inputLowpass, earlyBus, 0.34),
    createEarlyTap(ctx, inputLowpass, earlyBus, -0.24),
    createEarlyTap(ctx, inputLowpass, earlyBus, 0.47),
  ];

  inputLowpass.connect(lateInputBus);

  const convolverStage = createConvolverStage(ctx, lateInputBus, earlyBus);

  earlyBus.connect(wetHighpass);
  wetHighpass.connect(wetLowpass);
  wetLowpass.connect(wetPresence);
  wetPresence.connect(wetAir);
  wetAir.connect(wetLimiter);
  wetLimiter.connect(wetGain);
  wetGain.connect(masterLimiter);

  masterLimiter.connect(outputGain);
  outputGain.connect(output);

  const rack = {
    ctx,
    input,
    output,
    inputCompressor,
    compressorMakeupGain,
    dryTone,
    dryGain,
    wetGain,
    preDelay,
    inputHighpass,
    inputLowpass,
    earlyBus,
    lateInputBus,
    wetHighpass,
    wetLowpass,
    wetPresence,
    wetAir,
    wetLimiter,
    masterLimiter,
    outputGain,
    earlyTaps,
    convolverStage,
    currentPresetName: DEFAULT_PRESET,
    currentSettings: getAudioEffectPreset(DEFAULT_PRESET),
    currentReverbCharacter: 'medium',
    currentCompressionAmount: 0,
    currentBrightnessAmount: 5,
    destroyed: false,
  };

  applyAudioEffectPreset(rack, DEFAULT_PRESET, { immediate: true });

  return rack;
}

export function connectAudioEffectsRack(sourceNode, rack, destinationNode) {
  if (!sourceNode || !rack || !destinationNode) return;
  try {
    sourceNode.connect(rack.input);
  } catch {}
  try {
    rack.output.connect(destinationNode);
  } catch {}
}

export function disconnectAudioEffectsRack(sourceNode, rack, destinationNode) {
  if (sourceNode && rack) {
    try {
      sourceNode.disconnect(rack.input);
    } catch {}
  }
  if (rack && destinationNode) {
    try {
      rack.output.disconnect(destinationNode);
    } catch {}
  }
}

export function applyAudioEffectPreset(rack, presetName, opts = {}) {
  if (!rack || rack.destroyed) return null;

  const preset = getAudioEffectPreset(presetName);
  const now = rack.ctx.currentTime || 0;
  const immediate = !!opts.immediate;
  const smoothing = immediate ? 0.001 : clamp(opts.smoothing, 0.001, 1, 0.05);

  const reverbAmount = clamp(
    opts.reverbAmountOverride != null ? opts.reverbAmountOverride : preset.reverbAmount,
    0,
    10,
    0
  );

  const compressionAmount = clamp(
    opts.compressionAmountOverride != null ? opts.compressionAmountOverride : preset.compressionAmount,
    0,
    10,
    0
  );

  const brightnessAmount = clamp(
    opts.brightnessAmountOverride != null ? opts.brightnessAmountOverride : preset.brightnessAmount,
    0,
    10,
    5
  );

  const character = getCharacterSettings(
    opts.reverbCharacterOverride || preset.reverbCharacter || 'medium'
  );

  const compression = getCompressionSettings(compressionAmount);
  const brightness = getBrightnessSettings(brightnessAmount);

  const baseWetMix = mapReverbAmountToWetMix(reverbAmount);
  const wetMix = Math.min(0.65, Math.max(0, baseWetMix * brightness.wetMixMul));
  const earlyGain = mapReverbAmountToEarlyGain(reverbAmount);
  const lateSend = Math.min(1.2, mapReverbAmountToLateSend(reverbAmount) * brightness.convolverToneMul);
  const wetLowpassHz = Math.max(
    1800,
    Math.min(14000, mapReverbAmountToWetLowpass(reverbAmount, character.wetLowpass) + brightness.wetLowpassOffsetHz)
  );
  const inputLowpassHz = Math.max(1800, Math.min(12000, brightness.inputLowpassHz));
  const outputTrim = Math.max(
    0.68,
    mapReverbAmountToOutputTrim(reverbAmount, character.outputTrim, compressionAmount) + brightness.outputTrimOffset
  );

  setWetDry(rack, wetMix, now, smoothing);
  setParamValue(rack.inputCompressor.threshold, compression.threshold, now, smoothing);
  setParamValue(rack.inputCompressor.knee, compression.knee, now, smoothing);
  setParamValue(rack.inputCompressor.ratio, compression.ratio, now, smoothing);
  setParamValue(rack.inputCompressor.attack, compression.attack, now, smoothing);
  setParamValue(rack.inputCompressor.release, compression.release, now, smoothing);
  setParamValue(rack.compressorMakeupGain.gain, compression.makeup, now, smoothing);

  setParamValue(rack.dryTone.frequency, brightness.dryPresenceFreqHz, now, smoothing);
  setParamValue(rack.dryTone.gain, brightness.dryPresenceGain, now, smoothing);

  setParamValue(rack.preDelay.delayTime, character.preDelay, now, smoothing);
  setParamValue(rack.inputHighpass.frequency, character.inputHighpass, now, smoothing);
  setParamValue(rack.inputLowpass.frequency, inputLowpassHz, now, smoothing);

  setParamValue(rack.wetHighpass.frequency, character.wetHighpass, now, smoothing);
  setParamValue(rack.wetLowpass.frequency, wetLowpassHz, now, smoothing);
  setParamValue(rack.wetPresence.frequency, brightness.wetPresenceFreqHz, now, smoothing);
  setParamValue(rack.wetPresence.gain, brightness.wetPresenceGain, now, smoothing);
  setParamValue(rack.wetAir.frequency, brightness.airFreqHz, now, smoothing);
  setParamValue(rack.wetAir.gain, brightness.airGain, now, smoothing);

  setParamValue(rack.outputGain.gain, outputTrim, now, smoothing);
  setParamValue(rack.earlyBus.gain, character.wetTrim * brightness.earlyBusMul, now, smoothing);
  setParamValue(rack.lateInputBus.gain, 1, now, smoothing);
  setParamValue(rack.convolverStage.inputGain.gain, lateSend, now, smoothing);
  setParamValue(rack.convolverStage.toneHighpass.frequency, character.wetHighpass, now, smoothing);
  setParamValue(
    rack.convolverStage.toneLowpass.frequency,
    Math.max(1800, Math.min(14000, wetLowpassHz * brightness.convolverToneMul)),
    now,
    smoothing
  );
  setParamValue(rack.convolverStage.outputGain.gain, 1, now, smoothing);

  for (let i = 0; i < rack.earlyTaps.length; i++) {
    const tap = rack.earlyTaps[i];
    const spec = character.earlyTimes[i] != null ? character.earlyTimes[i] : character.earlyTimes[character.earlyTimes.length - 1];
    const gainMul = Math.pow(0.82, i);
    const pan = character.earlyPans[i] != null ? character.earlyPans[i] : 0;
    setParamValue(tap.delay.delayTime, spec, now, smoothing);
    setParamValue(tap.gain.gain, earlyGain * gainMul, now, smoothing);
    try {
      tap.panNode.setPan(pan, now, smoothing);
    } catch {}
  }

  try {
    rack.convolverStage.convolver.buffer = buildVocalImpulseResponse(rack.ctx, character, reverbAmount);
  } catch {}

  rack.currentPresetName = String(presetName || DEFAULT_PRESET).trim().toLowerCase() || DEFAULT_PRESET;
  rack.currentReverbCharacter = character.key;
  rack.currentCompressionAmount = compressionAmount;
  rack.currentBrightnessAmount = brightnessAmount;
  rack.currentSettings = {
    name: rack.currentPresetName,
    reverbAmount,
    reverbCharacter: character.key,
    compressionAmount,
    brightnessAmount,
  };

  return rack.currentSettings;
}

export function clearAudioEffectPreset(rack, opts = {}) {
  return applyAudioEffectPreset(rack, DEFAULT_PRESET, {
    immediate: !!opts.immediate,
    smoothing: opts.smoothing,
    reverbAmountOverride: 0,
    reverbCharacterOverride: 'medium',
    compressionAmountOverride: 0,
    brightnessAmountOverride: 5,
  });
}

export function destroyAudioEffectsRack(rack) {
  if (!rack || rack.destroyed) return;
  rack.destroyed = true;

  const nodes = [
    rack.input,
    rack.output,
    rack.inputCompressor,
    rack.compressorMakeupGain,
    rack.dryTone,
    rack.dryGain,
    rack.wetGain,
    rack.preDelay,
    rack.inputHighpass,
    rack.inputLowpass,
    rack.earlyBus,
    rack.lateInputBus,
    rack.wetHighpass,
    rack.wetLowpass,
    rack.wetPresence,
    rack.wetAir,
    rack.wetLimiter,
    rack.masterLimiter,
    rack.outputGain,
    rack.convolverStage && rack.convolverStage.inputGain,
    rack.convolverStage && rack.convolverStage.convolver,
    rack.convolverStage && rack.convolverStage.toneHighpass,
    rack.convolverStage && rack.convolverStage.toneLowpass,
    rack.convolverStage && rack.convolverStage.outputGain,
  ];

  for (const tap of rack.earlyTaps || []) {
    nodes.push(tap.delay, tap.gain);
    if (tap.panNode) {
      nodes.push(tap.panNode.input, tap.panNode.output);
    }
  }

  for (const node of nodes) {
    if (!node) continue;
    try {
      node.disconnect();
    } catch {}
  }

  for (const tap of rack.earlyTaps || []) {
    try {
      if (tap.panNode && typeof tap.panNode.disconnect === 'function') tap.panNode.disconnect();
    } catch {}
  }
}

export function createProcessedMediaStreamTrackFromTrack(inputTrack, options = {}) {
  const ctx = ensureAudioEffectsContext(options.ctx);
  if (!ctx || !inputTrack) {
    return {
      ctx: null,
      rack: null,
      source: null,
      destination: null,
      track: inputTrack || null,
      destroy() {},
      applyPreset() {},
      clearPreset() {},
      async resume() {},
    };
  }

  const stream = new MediaStream([inputTrack]);
  const source = ctx.createMediaStreamSource(stream);
  const destination = ctx.createMediaStreamDestination();
  const rack = createAudioEffectsRack(ctx);

  connectAudioEffectsRack(source, rack, destination);

  if (
    options.preset ||
    options.reverbAmountOverride != null ||
    options.reverbCharacterOverride ||
    options.compressionAmountOverride != null ||
    options.brightnessAmountOverride != null
  ) {
    applyAudioEffectPreset(rack, options.preset || DEFAULT_PRESET, {
      immediate: true,
      reverbAmountOverride: options.reverbAmountOverride,
      reverbCharacterOverride: options.reverbCharacterOverride,
      compressionAmountOverride: options.compressionAmountOverride,
      brightnessAmountOverride: options.brightnessAmountOverride,
    });
  }

  const outTrack = destination.stream.getAudioTracks()[0] || inputTrack;

  return {
    ctx,
    rack,
    source,
    destination,
    track: outTrack,
    async resume() {
      await resumeAudioEffectsContext(ctx);
    },
    applyPreset(presetName, opts = {}) {
      return applyAudioEffectPreset(rack, presetName, opts);
    },
    clearPreset(opts = {}) {
      return clearAudioEffectPreset(rack, opts);
    },
    destroy() {
      disconnectAudioEffectsRack(source, rack, destination);
      destroyAudioEffectsRack(rack);
      try {
        source.disconnect();
      } catch {}
      try {
        destination.disconnect();
      } catch {}
    },
  };
}

export function createEffectsRackFromTrack(inputTrack, options = {}) {
  return createProcessedMediaStreamTrackFromTrack(inputTrack, options);
}